import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CreditCard, RefreshCw, Clock, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PaymentPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Payment & Refund Policy | VietClinics</title>
        <meta name="description" content="VietClinics payment and refund policy — accepted payment methods, deposits, cancellation rules, refund eligibility and processing timelines." />
        <link rel="canonical" href="https://vietclinics.com/payment-policy" />
      </Helmet>
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] py-12">
        <div className="container mx-auto px-4 text-center">
          <CreditCard className="h-12 w-12 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Payment & Refund Policy
          </h1>
          <p className="text-white/80">Last updated: March 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          
          {/* Quick Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 shadow-sm text-center">
              <CreditCard className="h-8 w-8 text-[#1FA7A1] mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Flexible Payments</h3>
              <p className="text-sm text-gray-500">Pay at clinic or online</p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm text-center">
              <RefreshCw className="h-8 w-8 text-[#1FA7A1] mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Free Cancellation</h3>
              <p className="text-sm text-gray-500">24 hours before appointment</p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm text-center">
              <Clock className="h-8 w-8 text-[#1FA7A1] mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Quick Refunds</h3>
              <p className="text-sm text-gray-500">Within 7-14 business days</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
            <div className="prose prose-gray max-w-none">

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="h-6 w-6 text-[#1FA7A1]" />
                  Payment Options
                </h2>
                
                <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1. Pay at Clinic (Default)</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Most bookings on VietClinics do not require upfront payment. You pay directly at the clinic 
                  after receiving your treatment. This includes:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 mb-4">
                  <li>Cash (Vietnamese Dong)</li>
                  <li>Credit/Debit cards (Visa, Mastercard)</li>
                  <li>Bank transfer</li>
                  <li>Mobile payment (MoMo, ZaloPay - varies by clinic)</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2. Deposit Payment</h3>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Some clinics or treatments may require a deposit to confirm your booking:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1">
                  <li>Typically 20-50% of the treatment cost</li>
                  <li>Paid online through our secure payment gateway</li>
                  <li>Remaining balance paid at the clinic</li>
                  <li>Deposit is deducted from final payment</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">3. Full Payment Online</h3>
                <p className="text-gray-600 leading-relaxed">
                  For certain treatments or promotional packages, you may have the option to pay in full online. 
                  This may come with additional discounts or benefits.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <RefreshCw className="h-6 w-6 text-[#1FA7A1]" />
                  Cancellation Rules
                </h2>
                
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <h4 className="font-semibold text-green-800 mb-2">Free Cancellation</h4>
                  <p className="text-green-700 text-sm">
                    Cancel at least <strong>24 hours before</strong> your appointment for a full refund of any deposits paid.
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">Late Cancellation (Less than 24 hours)</h4>
                  <p className="text-yellow-700 text-sm">
                    Cancellations made less than 24 hours before the appointment may be subject to a 
                    cancellation fee of up to 50% of the deposit amount.
                  </p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                  <h4 className="font-semibold text-red-800 mb-2">No-Show</h4>
                  <p className="text-red-700 text-sm">
                    If you do not attend your appointment without prior notice, the full deposit may be forfeited. 
                    Repeated no-shows may result in account restrictions.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">How to Cancel</h3>
                <ol className="list-decimal pl-6 text-gray-600 space-y-1">
                  <li>Contact the clinic directly via WhatsApp or phone</li>
                  <li>Or cancel through your VietClinics account (if available)</li>
                  <li>Provide your booking reference number</li>
                  <li>Receive cancellation confirmation</li>
                </ol>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-6 w-6 text-[#1FA7A1]" />
                  Refund Eligibility
                </h2>
                
                <p className="text-gray-600 leading-relaxed mb-4">You may be eligible for a refund if:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>You cancel at least 24 hours before your appointment</li>
                  <li>The clinic cancels or cannot accommodate your booking</li>
                  <li>There is a significant service discrepancy from what was advertised</li>
                  <li>Double payment or payment error occurred</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mb-4"><strong>Refunds are NOT available for:</strong></p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Completed treatments (regardless of satisfaction)</li>
                  <li>No-shows or same-day cancellations (deposit may be forfeited)</li>
                  <li>Change of mind after receiving treatment</li>
                  <li>Dissatisfaction with treatment results (contact clinic directly)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="h-6 w-6 text-[#1FA7A1]" />
                  Refund Timeline
                </h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-gray-600">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 font-semibold text-gray-900">Payment Method</th>
                        <th className="text-left py-3 font-semibold text-gray-900">Refund Timeline</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3">Credit/Debit Card</td>
                        <td className="py-3">7-14 business days</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3">Bank Transfer</td>
                        <td className="py-3">5-7 business days</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3">VNPay</td>
                        <td className="py-3">3-5 business days</td>
                      </tr>
                      <tr>
                        <td className="py-3">Mobile Wallet (MoMo, ZaloPay)</td>
                        <td className="py-3">1-3 business days</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-gray-500 text-sm mt-4">
                  * Timeline begins after refund approval. Actual processing time may vary based on your 
                  bank or payment provider.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">How to Request a Refund</h2>
                <ol className="list-decimal pl-6 text-gray-600 space-y-2">
                  <li>Contact our support team at support@vietclinics.com</li>
                  <li>Provide your booking reference number</li>
                  <li>Explain the reason for your refund request</li>
                  <li>Attach any relevant documentation (if applicable)</li>
                  <li>Our team will review and respond within 2 business days</li>
                </ol>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Disputes</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you have a dispute regarding a payment or treatment, please contact our support team 
                  first. We will work with you and the clinic to resolve the issue fairly. For unresolved 
                  disputes, you may escalate through your payment provider's dispute resolution process.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-600 leading-relaxed">
                  For payment or refund inquiries:
                </p>
                <ul className="list-none mt-2 text-gray-600">
                  <li>Email: <a href="mailto:support@vietclinics.com" className="text-[#1FA7A1] hover:underline" data-testid="payment-support-email">support@vietclinics.com</a></li>
                  <li>Phone / WhatsApp: <a href="tel:+84386492508" className="text-[#1FA7A1] hover:underline" data-testid="payment-support-phone">+84 38 649 2508</a> (<a href="https://wa.me/84386492508" target="_blank" rel="noreferrer" className="text-[#1FA7A1] hover:underline" data-testid="payment-support-whatsapp">WhatsApp</a>)</li>
                </ul>
              </section>

            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentPolicyPage;
